/* Per-product maximum quantity (custom.maximum_quantity).
 *
 * Liquid emits the cap into each quantity input's max attribute (with the raw
 * cap mirrored in data-max) and renders a "(n of X)" note beside the selector
 * (snippets/wonderly-qty-max-note.liquid). This module:
 *   1. Clamps typed/pasted values to [min, max] in the CAPTURE phase, before
 *      QuantityInput (global.js), cart.js's debounced onChange, or the
 *      flexi-box handlers see the value — so /cart/change and /cart/add only
 *      ever receive capped quantities.
 *   2. Keeps every note in sync. Display rule: n = input.value, X = input.max
 *      (remaining-addable on the PDP after Dawn's cart subtraction, the cap
 *      elsewhere) — EXCEPT when the cap is exhausted (max <= 0): then show
 *      "(cap of cap)" from data-max, e.g. "(4 of 4)".
 *   3. Keeps collection-card steppers cart-aware: on every cartUpdate the
 *      per-variant cart quantities are refetched and each card's remaining
 *      max, Add button, and note are updated.
 *
 * Notes are paired to inputs by proximity (nearest shared ancestor), not by
 * id — quick-add modals duplicate Quantity-{section.id} ids. Cart-line inputs
 * (name="updates[]") are never silently clamped outside a user change: their
 * value is cart truth and may legitimately exceed a shrunken cap.
 */

(() => {
  const INPUT_SELECTOR = '.quantity__input, .wonderly-pc__qty-input, [data-flexi-qty]';
  const NOTE_SELECTOR = '[data-wonderly-qty-note]';
  // Every card input carries the variant id; only capped ones carry data-max.
  const CARD_INPUT_SELECTOR = '.wonderly-pc__qty-input[data-quantity-variant-id]';
  const IN_CART_BADGE_SELECTOR = '[data-in-cart-count]';

  // "N in cart" badge beside the card's add button.
  const syncInCartBadge = (input, count) => {
    const badge = input.form ? input.form.querySelector(IN_CART_BADGE_SELECTOR) : null;
    if (!badge) return;
    const template = count === 1 ? badge.dataset.inCartOne : badge.dataset.inCartOther;
    if (template) badge.textContent = template.replace('[count]', count);
    badge.hidden = count <= 0;
  };

  const scopedFind = (start, selector) => {
    let node = start.parentElement;
    for (let depth = 0; node && node !== document.body && depth < 6; depth += 1) {
      const found = node.querySelector(selector);
      if (found) return found;
      node = node.parentElement;
    }
    return null;
  };

  const clampValue = (input) => {
    const max = parseInt(input.max, 10);
    if (Number.isNaN(max)) return;
    const min = parseInt(input.min, 10) || 1;
    let value = parseInt(input.value, 10);
    if (Number.isNaN(value)) value = min;
    value = Math.max(Math.min(value, Math.max(max, 0)), Math.min(min, Math.max(max, 0)));
    if (String(value) !== input.value) input.value = value;
  };

  const syncNote = (input) => {
    const note = scopedFind(input, NOTE_SELECTOR);
    if (!note || !note.dataset.template) return;
    const max = parseInt(input.max, 10);
    const rawCap = parseInt(input.dataset.max, 10);
    if (Number.isNaN(max) && Number.isNaN(rawCap)) return;
    let shown = parseInt(input.value, 10) || 0;
    let cap = Number.isNaN(max) ? rawCap : max;
    // Cap exhausted (e.g. everything already in the cart): "(4 of 4)".
    if (!Number.isNaN(rawCap) && (Number.isNaN(max) || max <= 0)) {
      shown = rawCap;
      cap = rawCap;
    }
    note.textContent = note.dataset.template
      .replace('[count]', shown)
      .replace('[max]', Math.max(cap, 0));
  };

  const syncAll = (root, options) => {
    const scope = root && root.querySelectorAll ? root : document;
    const clamp = Boolean(options && options.clamp);
    scope.querySelectorAll(NOTE_SELECTOR).forEach((note) => {
      const input = scopedFind(note, INPUT_SELECTOR);
      if (!input) return;
      // Never silently clamp cart lines — their value is cart truth.
      if (clamp && !(input.name || '').startsWith('updates')) clampValue(input);
      syncNote(input);
    });
  };

  /* ---------- Collection-card steppers: live cart awareness ---------- */

  const refreshCardCaps = async () => {
    const cardInputs = document.querySelectorAll(CARD_INPUT_SELECTOR);
    if (!cardInputs.length) return;
    let cart;
    try {
      cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
    } catch (error) {
      return;
    }
    const inCart = {};
    cart.items.forEach((item) => {
      inCart[item.variant_id] = (inCart[item.variant_id] || 0) + item.quantity;
    });

    cardInputs.forEach((input) => {
      const variantId = input.dataset.quantityVariantId;
      if (!variantId) return;
      const quantityInCart = inCart[variantId] || 0;
      input.dataset.cartQuantity = quantityInCart;
      syncInCartBadge(input, quantityInCart);

      const rawCap = parseInt(input.dataset.max, 10);
      if (Number.isNaN(rawCap)) return;
      const remaining = Math.max(rawCap - quantityInCart, 0);
      input.max = remaining;
      clampValue(input);

      // Gate the card's Add button at the cap — via our own marker only, so
      // the sold-out disabled state is never touched.
      const form = input.form;
      const submit = form ? form.querySelector('[name="add"]') : null;
      if (submit) {
        if (remaining <= 0) {
          submit.setAttribute('disabled', 'disabled');
          submit.setAttribute('data-wonderly-capped', 'true');
        } else if (submit.hasAttribute('data-wonderly-capped')) {
          submit.removeAttribute('disabled');
          submit.removeAttribute('data-wonderly-capped');
        }
      }
      syncNote(input);
    });
  };

  /* ---------- Wiring ---------- */

  document.addEventListener(
    'change',
    (event) => {
      if (event.target.matches && event.target.matches(INPUT_SELECTOR)) {
        clampValue(event.target);
        syncNote(event.target);
      }
    },
    true
  );

  // Live n while typing; the hard clamp lands on change.
  document.addEventListener('input', (event) => {
    if (event.target.matches && event.target.matches(INPUT_SELECTOR)) syncNote(event.target);
  });

  // Dawn lifecycle: setQuantityBoundries publishes quantityUpdate on variant
  // change / cart subtraction; cart mutations publish cartUpdate. A PARTIAL
  // add (requested > stock) still changes the cart but product-form publishes
  // cartError instead of cartUpdate — refresh on that too, and nudge the
  // PDP's own quantity-rules refetch (Dawn skips it on the error path).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => syncAll());
    if (PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        syncAll();
        refreshCardCaps();
      });
    }
    if (PUB_SUB_EVENTS.cartError) {
      subscribe(PUB_SUB_EVENTS.cartError, () => {
        refreshCardCaps();
        document.querySelectorAll('product-info').forEach((info) => {
          if (!info.dataset.originalSection && typeof info.fetchQuantityRules === 'function') {
            info.fetchQuantityRules();
          }
        });
      });
    }
  }

  const init = () => {
    syncAll();
    refreshCardCaps();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.WonderlyQtyMax = { sync: syncAll, syncNote };
})();
